import { useKeycloak } from "@react-keycloak/web";
import { useSowContext } from "store/sow";
import {
  ActionTypes,
  ActivityItemStatusEnum,
  ActivityItemValidationStatusEnum,
  FlowStepsStatusEnum,
} from "utils/@types";
import {
  checkMessageErrors,
  compareStringsIgnoreCaseAndRemoveSpecialChars,
} from "utils/functions";
import ErrorOutlinedIcon from "@material-ui/icons/ErrorOutlined";
import {
  CloudDownload,
  CloudUpload,
  Gavel,
  RestorePage,
  Replay,
} from "@material-ui/icons";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSuperUserManualValidation } from "services/input-core/manual-validation/super-user-validation";
import { useResetActivityItemValidation } from "services/input-core/activity-items/reset-validation";
import { useResetActivityItem } from "services/input-core/activity-items/reset-activity-item";
import { useWorkflowProcess } from "services/input-core/workflow/set-workflow-process";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

import { ActivityItemWorker } from "services/input-core/work-items/@types";
import { Box, Button } from "@mui/material";
import GridItemTS from "components/UI/Grid/GridItemTS";
import ConfirmReopenModal from "components/Modals/UploadModal/ConfirmReopenModal";
import UploadModal from "components/Modals/UploadModal";
import RejectedModal from "components/Modals/RejectedModal";
import ApprovalModal from "components/Modals/ApprovalModal";
import ExpirationDateModal from "components/Modals/ExpirationDateModal";
import { useDeleteActivityItem } from "services/input-core/activity-items/delete-activity-item";
import { Delete } from "@mui/icons-material";
import DeleteActivityItemModal from "components/Modals/ConfirmActivityDelete";
import { ActivityItem } from "store/activityItem/activityItemContext.types";
import { useActivityItemContext } from "store/activityItem";
import { useWorkItemManualValidation } from "services/input-core/work-items/manual-validate";

interface ActivityItemsProps {
  refID?: string;
  profile: "WORKER" | "SOW";
  entityDate?: {
    start: string;
    end: string;
  };
  supplierCode: string;
}
export const ActionButtons = ({
  profile,
  entityDate,
  refID,
  supplierCode,
}: ActivityItemsProps) => {
  const activityItem = useSowContext((ctx) => ctx.activityItemDetails);
  const getActivityDetails = useSowContext((ctx) => ctx.getActivityDetails);

  const { selectActivity } = useActivityItemContext((state) => ({
    selectActivity: state.selectActivity,
  }));

  useEffect(() => {
    selectActivity(activityItem);
  }, [activityItem, selectActivity]);

  const config = useSowContext((ctx) => ctx.activityFlow);
  const { keycloak } = useKeycloak();
  const { t } = useTranslation([
    "activity-item-table",
    "list-errors",
    "list-messages",
  ]);
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadType, setUploadType] = useState(false);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [checkReopenModalOpen, setCheckReopenModalOpen] = useState(false);
  const [rejectedModalOpen, setRejectedModalOpen] = useState(false);
  const [expirationDateModalOpen, setExpirationDateModalOpen] = useState(false);
  const [
    deleteActivityItemModalOpen,
    setDeleteActivityItemModalOpen,
  ] = useState(false);

  function refetchActivityItem() {
    getActivityDetails(activityItem);
  }

  const {
    mutate: superManualValidation,
    isLoading: isSavingSuperManualValidation,
  } = useSuperUserManualValidation();

  const { mutate: submitManualValidation } = useWorkItemManualValidation();

  const {
    mutate: resetActivityValidation,
    isLoading: isResetingActivityValidation,
  } = useResetActivityItemValidation();

  const {
    mutate: resetActivityItem,
    isLoading: isResetingActivityItem,
  } = useResetActivityItem();

  const {
    mutate: workflowProcess,
    isLoading: isWorkflowProcess,
  } = useWorkflowProcess();

  const {
    mutate: deleteActivityItem,
    isLoading: isDeletingActivityItem,
  } = useDeleteActivityItem();

  const handleResetActivityItem = (activityItemCode: any) => {
    toast.success(t("info-reset-started", { ns: "list-messages" }));

    resetActivityItem(
      {
        zIdentifier: activityItemCode.zIdentifier,
        activityItemCode: activityItemCode.activityItemCode,
        fieldglassId: activityItemCode.fieldglassId,
      },
      {
        onSuccess: () => {
          toast.success(t("success-reset", { ns: "list-messages" }));

          // refetchActivityItems();
          refetchActivityItem();
        },
        onError: () => {
          toast.error(t("reset-fail"));
        },
      },
    );
  };

  const handleResetActivityValidation = () => {
    toast.info(`
      ${t("info-redefining-validation", {
        ns: "list-messages",
      })}${activityItem.activityItemCode}`);

    resetActivityValidation(
      {
        zIdentifier: activityItem.zIdentifier,
        activityItemCode: activityItem.activityItemCode,
        fieldglassId: activityItem.fieldglassId,
      },
      {
        onSuccess: () => {
          toast.success(t("success-reopen", { ns: "list-messages" }));
          refetchActivityItem();
          queryClient.invalidateQueries(["activity-items-sow"]);
          setCheckReopenModalOpen(false);
        },
        onError: (error: any) => {
          toast.error(
            t(
              checkMessageErrors(
                error?.response?.data?.error,
                "ERR_REOPEN_DEFAULT",
              ),
              { ns: "list-errors" },
            ),
          );
        },
      },
    );
  };

  const handleWorkflowProcess = (activityItemId: string) => {
    toast.info(t("info-process-activity", { ns: "list-messages" }));
    workflowProcess(
      {
        activityItemId: activityItemId,
      },
      {
        onSuccess: () => {
          toast.success(t("success-process-activity", { ns: "list-messages" }));
          refetchActivityItem();
          queryClient.invalidateQueries(["activity-items-sow"]);
        },
        onError: (error: any) => {
          toast.warning(
            t(
              checkMessageErrors(
                error?.response?.data?.error,
                "ERR_PROCESS_ACTIVITY_DEFAULT",
              ),
              { ns: "list-errors" },
            ),
          );
        },
      },
    );
  };

  const handleDeleteActivityItem = () => {
    toast.success(t("info-delete-started", { ns: "list-messages" }));

    deleteActivityItem(activityItem._id, {
      onSuccess: () => {
        toast.success(t("success-delete", { ns: "list-messages" }));
        setDeleteActivityItemModalOpen(false);
      },
      onError: () => {
        toast.error(t("fail-delete", { ns: "list-messages" }));
        setDeleteActivityItemModalOpen(false);
      },
    });
  };

  const isFirstStep = () => {
    return (
      activityItem?.validationStep === undefined ||
      activityItem?.validationStep === null ||
      (activityItem?.validationStep === 0 &&
        activityItem?.validationStatus !==
          ActivityItemValidationStatusEnum.REJECTED)
    );
  };

  const handleSuperUserApproval = (
    activityItem: ActivityItem,
    validationExpirationDate: Date,
    reason: string,
    hasInfiniteLifetime = false,
    validationExpirationStartDate?: Date,
  ) => {
    activityItem?.hasSuperUserManualCompletion
      ? superManualValidation(
          {
            reason,
            fieldglassId: activityItem.fieldglassId,
            activityItemCode: activityItem.activityItemCode,
            subscriptionId: activityItem.subscription,
            zIdentifier: activityItem.zIdentifier,
            ...(hasInfiniteLifetime
              ? {
                  hasInfiniteLifetime,
                }
              : {
                  validationExpirationDate,
                  ...(activityItem?.hasStartDate && {
                    validationExpirationStartDate,
                  }),
                }),
          },
          {
            onSuccess: () => {
              toast.success(t("success-validate", { ns: "list-messages" }));

              queryClient.invalidateQueries(["activity-items-sow", refID]);
              setExpirationDateModalOpen(false);
            },
            onError: (error: any) => {
              toast.error(
                t(
                  checkMessageErrors(
                    error?.response?.data?.error,
                    "ERR_VALIDATE_DEFAULT",
                  ),
                  { ns: "list-errors" },
                ),
              );
            },
          },
        )
      : submitManualValidation(
          {
            workItemID: activityItem?.workitems[0]?._id,
            reason,
            activityItemCode: activityItem.activityItemCode,
            fieldglassId: activityItem.fieldglassId,
            ...(activityItem?.workitems[0]?._id
              ? {}
              : {
                  zIdentifier: activityItem.zIdentifier,
                  profile: profile,
                  supplierCode: supplierCode,
                }),
            ...(hasInfiniteLifetime
              ? {
                  hasInfiniteLifetime,
                }
              : {
                  validationExpirationDate,
                  ...(activityItem?.hasStartDate && {
                    validationExpirationStartDate,
                  }),
                }),
          },
          {
            onSuccess: () => {
              toast.success(
                t("success-manual-validation", {
                  ns: "manual-validation-modal",
                }),
                {
                  className: "success-toast-message",
                },
              );
              queryClient.invalidateQueries(["activity-items-sow", refID]);
              setExpirationDateModalOpen(false);
            },
            onError: (error: any) => {
              toast.error(
                t(
                  checkMessageErrors(
                    error?.response?.data?.error,
                    "ERR_MANUAL_VALIDATION_DEFAULT",
                  ),
                  { ns: "list-errors" },
                ),
              );
            },
          },
        );
  };

  const renderButtonsByStepAndRoles = () => {
    const isReopener = keycloak.hasRealmRole("Reopen Activity");
    const isDeleteAvailable = keycloak.hasRealmRole("Delete Activity");
    const isProcessAvailable = keycloak.hasRealmRole("Process Activity");
    const isSuperUser = keycloak.hasRealmRole("superUser");
    const isApprover = keycloak.hasRealmRole("approver");
    const isOnlyBuyer =
      keycloak.hasResourceRole("buyer", "uverify") &&
      !isApprover &&
      !isSuperUser;
    const isSupplier = keycloak.hasResourceRole("supplier", "uverify");
    const buttons = [] as JSX.Element[];

    if (activityItem?.actions?.includes(ActionTypes.APPROVE_ACTIVITY)) {
      if (isApprover) {
        buttons.push(
          <Button
            size="small"
            variant="outlined"
            style={{
              display: "flex",
              alignItems: "center",
              borderRadius: "5px",
            }}
            onClick={() => {
              // setActivityItem(activityItem);
              setApprovalModalOpen(true);
            }}
            data-cy="approve-modal-button"
          >
            <Gavel style={{ fontSize: "1.2rem" }} />
            {t("Validate Documents")}
          </Button>,
        );
      }
    }

    if (
      activityItem?.actions?.includes(ActionTypes.RESET_ACTIVITY) &&
      isOnlyBuyer
    ) {
      buttons.push(
        <Button
          size="small"
          style={{
            display: "flex",
            alignItems: "center",
            borderRadius: "5px",
          }}
          onClick={() => {
            handleResetActivityItem(activityItem);
          }}
          disabled={isResetingActivityItem}
          data-cy="reset-activity-button"
        >
          <RestorePage style={{ fontSize: "1.2rem" }} />
          {t("Reset Activity")}
        </Button>,
      );
    }

    if (activityItem?.actions?.includes(ActionTypes.UPLOAD_DOCUMENTS)) {
      if (isFirstStep()) {
        const waitingForDownloadStep = config?.validationSteps?.find(
          (step: any) =>
            compareStringsIgnoreCaseAndRemoveSpecialChars(
              step?.name,
              FlowStepsStatusEnum.CHANGE_ACTIVITY_STATUS,
            ),
        );

        if (
          (waitingForDownloadStep?.executedByRole?.includes("supplier") &&
            isSupplier) ||
          (waitingForDownloadStep?.executedByRole?.includes("buyer") &&
            isOnlyBuyer)
        ) {
          buttons.push(
            <Button
              size="small"
              variant="outlined"
              style={{
                display: "flex",
                alignItems: "center",
                borderRadius: "5px",
              }}
              onClick={() => {
                setModalOpen(true);
                setUploadType(true);
              }}
              data-cy="upload-documents-button"
            >
              <CloudUpload style={{ fontSize: "1.2rem" }} />
              {t("upload-documents")}
            </Button>,
          );
        }
      }

      if (
        (compareStringsIgnoreCaseAndRemoveSpecialChars(
          activityItem?.validationStatus,
          ActivityItemValidationStatusEnum.SUPPLIER_UPLOADING_DOCUMENTS,
        ) &&
          isSupplier) ||
        (compareStringsIgnoreCaseAndRemoveSpecialChars(
          activityItem?.validationStatus,
          ActivityItemValidationStatusEnum.REJECTED,
        ) &&
          config?.flowType !== "AUTHORIZATION" &&
          isSupplier) ||
        (compareStringsIgnoreCaseAndRemoveSpecialChars(
          activityItem?.validationStatus,
          ActivityItemValidationStatusEnum.REJECTED,
        ) &&
          isOnlyBuyer &&
          config?.flowType === "AUTHORIZATION")
      ) {
        buttons.push(
          <Button
            size="small"
            variant="outlined"
            style={{
              display: "flex",
              alignItems: "center",
              borderRadius: "5px",
            }}
            onClick={() => {
              setUploadType(true);
              setModalOpen(true);
            }}
            data-cy="upload-documents-button"
          >
            <CloudUpload style={{ fontSize: "1.2rem" }} />
            {t("upload-documents")}
          </Button>,
        );
      }
    }

    if (
      activityItem?.actions?.includes(ActionTypes.DOWNLOAD_DOCUMENTS) &&
      isSupplier
    ) {
      buttons.push(
        <Button
          size="small"
          variant="outlined"
          style={{
            display: "flex",
            alignItems: "center",
            borderRadius: "5px",
          }}
          onClick={() => {
            setModalOpen(true);
          }}
          data-cy="download-documents-button"
        >
          <CloudDownload style={{ fontSize: "1.2rem" }} />
          {t("download-documents")}
        </Button>,
      );
    }

    if (activityItem?.actions?.includes(ActionTypes.REASON)) {
      buttons.push(
        <>
          <ErrorOutlinedIcon style={{ fontSize: "1.2rem" }} />
          {t("rejected")}
        </>,
      );
    }

    if (
      isSuperUser &&
      !compareStringsIgnoreCaseAndRemoveSpecialChars(
        activityItem?.validationStatus,
        ActivityItemValidationStatusEnum.APPROVED,
      ) &&
      !compareStringsIgnoreCaseAndRemoveSpecialChars(
        activityItem?.validationStatus,
        ActivityItemValidationStatusEnum.FINISHED,
      )
    ) {
      buttons.push(
        <Button
          size="small"
          variant="outlined"
          style={{
            display: "flex",
            alignItems: "center",
            borderRadius: "5px",
          }}
          onClick={() => {
            setExpirationDateModalOpen(true);
          }}
          data-cy="approve-documents-button"
        >
          <Gavel style={{ fontSize: "1.2rem" }} />
          {t("approve-documents")}
        </Button>,
      );
    }

    if (
      (isReopener || isSuperUser) &&
      compareStringsIgnoreCaseAndRemoveSpecialChars(
        activityItem?.validationStatus,
        ActivityItemValidationStatusEnum.APPROVED,
      ) &&
      compareStringsIgnoreCaseAndRemoveSpecialChars(
        activityItem?.activityItemStatus,
        ActivityItemStatusEnum.COMPLETED,
      )
    ) {
      buttons.push(
        <Button
          variant="outlined"
          size="small"
          style={{
            display: "flex",
            alignItems: "center",
            borderRadius: "5px",
          }}
          disabled={isResetingActivityValidation}
          onClick={() => {
            setCheckReopenModalOpen(true);
          }}
          data-cy="reopen-button"
        >
          <RestorePage style={{ fontSize: "1.2rem" }} />
          {t("reOpen")}
        </Button>,
      );
    }

    if (
      compareStringsIgnoreCaseAndRemoveSpecialChars(
        activityItem?.activityItemStatus,
        ActivityItemStatusEnum.PENDING,
      ) &&
      isDeleteAvailable
    ) {
      buttons.push(
        <Button
          disabled={isDeletingActivityItem}
          onClick={() => {
            setDeleteActivityItemModalOpen(true);
          }}
          data-cy="delete-button"
        >
          <Delete style={{ fontSize: "1.2rem" }} />
          {t("delete")}
        </Button>,
      );
    }

    if (isProcessAvailable) {
      buttons.push(
        <Button
          size="small"
          variant="outlined"
          style={{
            display: "flex",
            alignItems: "center",
            borderRadius: "5px",
          }}
          disabled={isWorkflowProcess}
          onClick={() => {
            handleWorkflowProcess(activityItem?._id);
          }}
          data-cy="process-activity-button"
        >
          <Replay style={{ fontSize: "1.2rem" }} />
          {t("process-activity")}
        </Button>,
      );
    }

    return <Box style={{ display: "flex", gap: "1rem" }}>{buttons}</Box>;
  };

  return (
    <GridItemTS
      key="beltrame-activity-item-table-grid"
      data-cy="beltrame-activity-item-table"
    >
      {renderButtonsByStepAndRoles()}
      {checkReopenModalOpen && (
        <ConfirmReopenModal
          isDeleting={isResetingActivityValidation}
          onClose={() => {
            setCheckReopenModalOpen(false);
          }}
          modalOpen={checkReopenModalOpen}
          onConfirm={() => handleResetActivityValidation()}
          data-cy="confirm-reopen-modal"
        />
      )}

      {modalOpen && (
        <UploadModal
          profile={profile}
          modalOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          fieldglassId={refID}
          uploadType={uploadType}
          setUploadType={setUploadType}
          entityDate={entityDate}
          data-cy="upload-modal"
        />
      )}

      {rejectedModalOpen && (
        <RejectedModal
          modalOpen={rejectedModalOpen}
          onClose={() => {
            setRejectedModalOpen(false);
          }}
          rejectedReason={activityItem}
          data-cy="rejected-modal"
        />
      )}

      {approvalModalOpen && (
        <ApprovalModal
          profile={profile}
          modalOpen={approvalModalOpen}
          onClose={() => {
            setApprovalModalOpen(false);
          }}
          fieldglassId={refID}
          entityDate={entityDate}
          data-cy="approval-modal"
        />
      )}

      {expirationDateModalOpen && (
        <ExpirationDateModal
          isUploading={isSavingSuperManualValidation}
          modalOpen={expirationDateModalOpen}
          onClose={() => {
            setExpirationDateModalOpen(false);
          }}
          entityDate={entityDate}
          onConfimSuperUser={handleSuperUserApproval}
          data-cy="expiration-date-modal"
        />
      )}

      {deleteActivityItemModalOpen && (
        <DeleteActivityItemModal
          onConfirm={handleDeleteActivityItem}
          isDeleting={isDeletingActivityItem}
          onClose={() => {
            setDeleteActivityItemModalOpen(false);
          }}
          modalOpen={deleteActivityItemModalOpen}
          data-cy="delete-activity-item-modal"
        />
      )}
    </GridItemTS>
  );
};
